import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Hook point for telemetry — kept silent here on purpose.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div
          role="alert"
          className="flex min-h-screen items-center justify-center bg-bg p-8 text-center"
        >
          <div className="max-w-md">
            <p
              className="mb-2 font-mono uppercase tracking-widest text-text-mute"
              style={{ fontSize: 10, letterSpacing: '0.18em' }}
            >
              ERROR
            </p>
            <h1 className="mb-4 font-serif text-text" style={{ fontSize: 32 }}>
              Algo se rompió.
            </h1>
            <p className="mb-6 text-text-dim">
              Un fallo inesperado interrumpió la app. Intenta recargar o volver atrás.
            </p>
            <button
              onClick={this.reset}
              className="rounded-md border border-border bg-surface px-4 py-2 text-sm text-text transition hover:bg-surface-2"
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
